<template>
  <div class="avatar-media-selector">
    <avatar-file-pond-uploader
      ref="uploaderRef"
      :model-value="currentModelValue"
      :server-url="serverUrl"
      :accepted-file-types="acceptedFileTypes"
      :max-file-size="maxFileSize"
      :placeholder-text="placeholderText"
      :csrf-token="csrfToken"
      @click="handleAvatarClick"
      @update:model-value="handleModelUpdate"
      @upload-success="handleUploadSuccess"
      @upload-error="emit('upload-error', $event)"
      @remove="handleRemove"
    >
      <template #placeholder>
        <slot name="placeholder">
          <f7-icon f7="camera_fill" size="32" color="gray"></f7-icon>
          <span class="avatar-label">{{ placeholderText }}</span>
        </slot>
      </template>
    </avatar-file-pond-uploader>

    <!-- Media Gallery Sheet with Upload Button -->
    <f7-sheet
      v-model:opened="sheetOpened"
      style="height: 75%"
      swipe-to-close
      backdrop
      class="media-gallery-sheet"
    >
      <f7-toolbar>
        <div class="left">
          <f7-link sheet-close>
            <f7-icon f7="xmark_circle"></f7-icon>
          </f7-link>
        </div>
        <div class="title">{{ labels.selectFromGallery }}</div>
      </f7-toolbar>

      <f7-page-content>
        <div class="media-grid-container">
          <!-- Upload New Button -->
          <div class="media-grid-item upload-item" @click="handleUploadClick">
            <div class="upload-placeholder">
              <f7-icon f7="arrow_up_doc_fill" size="32" color="primary"></f7-icon>
              <span class="upload-label">{{ labels.uploadNew }}</span>
            </div>
          </div>

          <!-- Existing Media Grid -->
          <div
            v-for="(media, index) in imageMedia"
            :key="media.id || index"
            class="media-grid-item"
            :class="{ 'media-selected': isMediaSelected(media) }"
            @click="selectMedia(media)"
          >
            <img
              :src="media.url"
              :alt="`صورة ${index + 1}`"
              loading="lazy"
              class="media-thumbnail"
            />
            <div v-if="isMediaSelected(media)" class="selection-badge">
              <f7-icon f7="checkmark_circle_fill" color="white"></f7-icon>
            </div>
          </div>

          <!-- Empty State (only if no media at all) -->
          <div v-if="imageMedia.length === 0" class="empty-state">
            <f7-icon f7="photo" size="48" color="gray"></f7-icon>
            <p class="text-color-gray">{{ labels.noImagesAvailable }}</p>
          </div>
        </div>
      </f7-page-content>
    </f7-sheet>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import AvatarFilePondUploader from './AvatarFilePondUploader.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  availableMedia: { type: Array, default: () => [] },
  serverUrl: { type: String, default: '/filepond' },
  acceptedFileTypes: { type: Array, default: () => ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'] },
  maxFileSize: { type: String, default: '5MB' },
  placeholderText: { type: String, default: 'رفع صورة' },
  csrfToken: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  labels: {
    type: Object,
    default: () => ({
      selectFromGallery: 'اختر من الصور',
      availableImages: 'الصور المتاحة',
      noImagesAvailable: 'لا توجد صور متاحة',
      uploadNew: 'رفع صورة جديدة',
    }),
  },
});

const emit = defineEmits(['update:modelValue', 'upload-success', 'upload-error', 'remove', 'media-selected']);

const uploaderRef = ref(null);
const sheetOpened = ref(false);
const gallerySelectedUrl = ref('');

const hasAvailableMedia = computed(() => Array.isArray(props.availableMedia) && props.availableMedia.length > 0);

const imageMedia = computed(() => {
  if (!hasAvailableMedia.value) return [];
  return props.availableMedia.filter(media => media && media.mediaType === 'image' && media.url);
});

const currentModelValue = computed(() => gallerySelectedUrl.value || props.modelValue);

const handleAvatarClick = () => {
  if (props.disabled) return;
  sheetOpened.value = true;
};

const handleUploadClick = () => {
  sheetOpened.value = false;
  setTimeout(() => {
    if (uploaderRef.value?.nativeFileInput) {
      uploaderRef.value.nativeFileInput.click();
    }
  }, 300);
};

const selectMedia = (media) => {
  gallerySelectedUrl.value = media.url;
  emit('update:modelValue', media.url);
  emit('media-selected', { media, url: media.url });
  sheetOpened.value = false;
  f7.toast.create({
    text: 'تم اختيار الصورة',
    position: 'center',
    closeTimeout: 1500,
    icon: '<i class="f7-icons">checkmark_circle_fill</i>',
  }).open();
};

const isMediaSelected = (media) => {
  return gallerySelectedUrl.value === media.url || props.modelValue === media.url;
};

const handleModelUpdate = (value) => {
  gallerySelectedUrl.value = '';
  emit('update:modelValue', value);
};

const handleUploadSuccess = (data) => {
  emit('upload-success', data);
};

const handleRemove = () => {
  gallerySelectedUrl.value = '';
  emit('remove');
};

const clear = () => {
  gallerySelectedUrl.value = '';
  if (uploaderRef.value) uploaderRef.value.clear();
};

defineExpose({
  clear,
  openGallery: () => { sheetOpened.value = true; },
});
</script>

<style scoped>
.avatar-media-selector {
    position: relative;
    display: inline-block;
}

.media-gallery-sheet :deep(.toolbar) {
    --f7-toolbar-height: 52px;
}

.media-gallery-sheet :deep(.toolbar .title) {
    font-size: 16px;
    font-weight: 600;
}

.media-grid-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 16px;
}

.media-grid-item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s;
    background: var(--f7-color-gray-200);
}

.media-grid-item:active {
    transform: scale(0.95);
}

.media-grid-item.media-selected {
    border-color: var(--f7-theme-color);
    box-shadow: 0 0 0 2px rgba(var(--f7-theme-color-rgb), 0.2);
}

.upload-item {
    border: 2px dashed var(--f7-theme-color);
    background: rgba(var(--f7-theme-color-rgb), 0.05);
}

.upload-item:active {
    background: rgba(var(--f7-theme-color-rgb), 0.1);
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 6px;
    padding: 12px;
}

.upload-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--f7-theme-color);
    text-align: center;
    line-height: 1.2;
}

.media-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.selection-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 28px;
    height: 28px;
    background: var(--f7-theme-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.selection-badge :deep(.icon) {
    font-size: 20px;
}

.empty-state {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 20px;
    text-align: center;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.avatar-label {
    font-size: 11px;
    line-height: 1.2;
    color: var(--f7-text-color);
    font-weight: 500;
}

@media (max-width: 380px) {
    .media-grid-container {
        gap: 6px;
        padding: 12px;
    }
    .upload-label {
        font-size: 10px;
    }
}
</style>
